import { useEffect, useRef, useState } from "react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/brand`;
const PARTS = ["base", "legl", "legr", "torso", "fore", "canpart"];
const SRC = Object.fromEntries(PARTS.map((p) => [p, `${BASE}/kenny-${p}.png`]));
const DUR = 8100;

// joint pivots as fractions of the 575x849 character canvas
const HIP_L = "34% 55.4%";
const HIP_R = "57.4% 55.4%";
const ELBOW = "72.2% 24.1%";
const TIP = { x: 0.948, y: 0.224 };      // can tip, arm raised
const TIP_DOWN = { x: 0.85, y: 0.33 };   // can tip, arm lowered

export const SplashIntro = () => {
  const [show, setShow] = useState(() => {
    try {
      const forced = new URLSearchParams(window.location.search).has("intro");
      if (!forced && sessionStorage.getItem("akdp-splash")) return false;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
      return true;
    } catch {
      return false;
    }
  });

  if (!show) return null;
  return (
    <SplashScene
      onDone={() => {
        try { sessionStorage.setItem("akdp-splash", "1"); } catch {}
        setShow(false);
      }}
    />
  );
};

const SplashScene = ({ onDone }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const rigRef = useRef(null);
  const legLRef = useRef(null);
  const legRRef = useRef(null);
  const foreRef = useRef(null);
  const canPartRef = useRef(null);
  const canRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const rig = rigRef.current;
    const legL = legLRef.current;
    const legR = legRRef.current;
    const fore = foreRef.current;
    const canPart = canPartRef.current;
    const can = canRef.current;
    const container = containerRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    canvas.width = vw * dpr;
    canvas.height = vh * dpr;
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, vw, vh);
    ctx.globalCompositeOperation = "destination-out";
    document.body.style.overflow = "hidden";

    PARTS.forEach((p) => { const i = new Image(); i.src = SRC[p]; });

    const charH = Math.min(vh * 0.62, 620);
    const charW = charH * (575 / 849);
    rig.style.height = `${charH}px`;
    rig.style.width = `${charW}px`;
    const leftMax = Math.min(vw * 0.46, vw - charW * 0.98);

    const tipPos = (left, tip) => ({ x: left + charW * tip.x, y: vh - charH + charH * tip.y });

    const splat = (x, y, r, n) => {
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2;
        const d = Math.random() * r;
        ctx.beginPath();
        ctx.arc(x + Math.cos(a) * d, y + Math.sin(a) * d * 0.7, Math.random() * r * 0.45 + 3, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const ease = (t) => 1 - Math.pow(1 - t, 3);

    let raf;
    let start;
    let dropped = false;
    let canStart = 0;
    let canX = 0;
    let canY = 0;
    let done = false;
    let foreCur = -18;
    let legAmp = 13;

    const finish = () => {
      if (done) return;
      done = true;
      document.body.style.overflow = "";
      onDone();
    };

    const frame = (now) => {
      if (!start) start = now;
      const t = now - start;

      let left;
      let legTarget;
      let foreBase;
      let shake = 0;

      if (t < 1100) {
        // walks on, legs stepping
        const p = ease(t / 1100);
        left = -0.65 * charW + p * (vw * 0.04 + 0.65 * charW);
        rig.style.opacity = String(p);
        legTarget = 13;
        foreBase = -18;
      } else if (t < 4300) {
        // plants feet, raises the can, sprays a tight band revealing the site
        const p = (t - 1100) / 3200;
        left = vw * 0.04 + ease(p) * (leftMax - vw * 0.04);
        legTarget = t < 1450 ? 0 : 0;
        foreBase = 0;
        const h = tipPos(left, TIP);
        splat(h.x + 8 + Math.random() * 46, h.y + (Math.random() - 0.5) * vh * 0.3, 34, 14);
        splat(h.x + Math.random() * 80, h.y + (Math.random() - 0.5) * vh * 0.22, 48, 8);
        splat(h.x - 60 - Math.random() * 120, h.y + (Math.random() - 0.5) * vh * 0.36, 42, 7);
      } else if (t < 5700) {
        // shakes the can at the elbow — body planted, sputtering
        left = leftMax;
        legTarget = 0;
        foreBase = 0;
        shake = Math.sin(t / 24) * 12;
        if (Math.random() < 0.25) {
          const h = tipPos(left, TIP);
          splat(h.x + Math.random() * 24, h.y + (Math.random() - 0.5) * 30, 7, 4);
        }
      } else if (t < 7300) {
        // drops the can and walks off with swagger; can spins on the ground
        if (!dropped) {
          dropped = true;
          canPart.style.opacity = "0";
          const h = tipPos(leftMax, TIP_DOWN);
          canX = Math.min(h.x - 13, vw - 50);
          canY = h.y;
          canStart = t;
          can.style.opacity = "1";
        }
        const p = (t - 5700) / 1600;
        left = leftMax + ease(p) * vw * 0.85;
        legTarget = 13;
        foreBase = -18;

        const ct = t - canStart;
        if (ct < 650) {
          const fp = ct / 650;
          can.style.transform = `translate(${canX}px, ${canY + fp * fp * (vh - 70 - canY)}px) rotate(${fp * 540}deg)`;
        } else {
          const sp = Math.min((ct - 650) / 1500, 1);
          can.style.transform = `translate(${canX + sp * 26}px, ${vh - 70}px) rotate(${540 + sp * 430}deg) scale(${1 - sp * 0.15})`;
          can.style.opacity = String(1 - Math.max(0, sp - 0.6) / 0.4);
        }
      } else {
        const p = Math.min((t - 7300) / 800, 1);
        container.style.opacity = String(1 - p);
        left = vw * 1.2;
        legTarget = 0;
        foreBase = -18;
      }

      // smooth joint motion
      foreCur += (foreBase - foreCur) * 0.14;
      legAmp += (legTarget - legAmp) * 0.12;

      const swing = Math.sin(t / 130) * legAmp;
      legL.style.transform = `rotate(${swing}deg)`;
      legR.style.transform = `rotate(${-swing}deg)`;
      const bob = Math.abs(Math.cos(t / 130)) * legAmp * 0.35;
      const lean = legAmp > 1 ? -2 + Math.sin(t / 260) * 1.8 : 0;
      rig.style.transform = `translateX(${left}px) translateY(${-bob}px) rotate(${lean}deg)`;
      const foreAngle = foreCur + shake;
      fore.style.transform = `rotate(${foreAngle}deg)`;
      canPart.style.transform = `rotate(${foreAngle}deg)`;

      if (t < DUR) raf = requestAnimationFrame(frame);
      else finish();
    };

    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = "";
    };
  }, [onDone]);

  const partCls = "absolute inset-0 w-full h-full select-none pointer-events-none";

  return (
    <div ref={containerRef} className="fixed inset-0 z-[200]" data-testid="splash-intro" role="dialog" aria-label="Intro animation">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
      <div ref={rigRef} className="absolute bottom-0 left-0" style={{ opacity: 0 }}>
        <img src={SRC.base} alt="" aria-hidden="true" draggable={false} className={partCls} />
        <img ref={legLRef} src={SRC.legl} alt="" aria-hidden="true" draggable={false} className={partCls} style={{ transformOrigin: HIP_L }} />
        <img ref={legRRef} src={SRC.legr} alt="" aria-hidden="true" draggable={false} className={partCls} style={{ transformOrigin: HIP_R }} />
        <img src={SRC.torso} alt="" aria-hidden="true" draggable={false} className={partCls} />
        <img ref={foreRef} src={SRC.fore} alt="" aria-hidden="true" draggable={false} className={partCls} style={{ transformOrigin: ELBOW }} />
        <img ref={canPartRef} src={SRC.canpart} alt="" aria-hidden="true" draggable={false} className={partCls} style={{ transformOrigin: ELBOW }} />
      </div>
      <div ref={canRef} className="absolute top-0 left-0 w-[26px] h-[44px] pointer-events-none" style={{ opacity: 0 }} data-testid="splash-can">
        <div className="absolute left-1/2 -translate-x-1/2 -top-[4px] w-[9px] h-[5px] bg-pink" />
        <div className="absolute inset-x-0 top-0 h-[10px] bg-neutral-600 rounded-t-[3px]" />
        <div className="absolute inset-x-0 top-[9px] bottom-0 bg-neutral-900 border border-neutral-600 rounded-b-[3px]" />
      </div>
      <button
        onClick={onDone}
        data-testid="splash-skip-button"
        className="absolute top-4 right-4 z-10 min-h-[44px] px-5 text-[11px] font-extrabold tracking-[0.25em] text-ink/60 hover:text-pink border border-ink/15 bg-white/70 backdrop-blur-sm transition-colors duration-200"
      >
        SKIP INTRO
      </button>
    </div>
  );
};
